/**
 * AdminJobReports — public "Report this job" queue (iter-135).
 * platform_admin only. SLA pill (24h) turns red once breached.
 */
import React, { useEffect, useState } from "react";
import { AlertOctagon, Clock, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REASON_LABELS = {
    scam_suspected: "Scam suspected",
    misleading: "Misleading",
    discriminatory: "Discriminatory",
    other: "Other",
};

export default function AdminJobReports() {
    const [rows, setRows] = useState([]);
    const [slaHours, setSlaHours] = useState(24);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/job-reports");
            setRows(data.results || []);
            setSlaHours(data.sla_hours || 24);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load");
        }
    };
    useEffect(() => { load(); }, []);

    const act = async (id, status) => {
        const note = status === "resolved" || status === "dismissed"
            ? (window.prompt(`Note for '${status}' (optional)`) || "")
            : "";
        setBusy(true);
        try {
            await api.patch(`/admin/job-reports/${id}`, { status, resolution_note: note });
            toast.success(`Report ${status}.`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="admin-job-reports-page">
            <div className="max-w-6xl mx-auto p-6">
                <div className="inline-flex items-center gap-2 text-xs uppercase font-semibold text-rose-700 dark:text-rose-400">
                    <AlertOctagon size={14} /> Trust &amp; Safety
                </div>
                <h1 className="mt-1 font-heading text-3xl font-semibold">Job Reports</h1>
                <p className="text-muted-foreground text-sm">
                    <span className="inline-flex items-center gap-1">
                        <Clock size={12} /> SLA: review within {slaHours} hours.
                    </span>
                </p>

                <div className="mt-6 rounded-2xl border border-border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                            <tr>
                                <th className="text-left p-3">Job</th>
                                <th className="text-left p-3">Reason</th>
                                <th className="text-left p-3">Age</th>
                                <th className="text-left p-3">SLA</th>
                                <th className="text-left p-3">Details</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {rows.length === 0 && (
                                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No open reports.</td></tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`report-row-${r.id}`}>
                                    <td className="p-3">
                                        <div className="font-medium">{r.job_title || "—"}</div>
                                        <a href={`/jobs/${r.job_slug || r.job_id}`} target="_blank" rel="noreferrer"
                                           className="text-[11px] text-primary inline-flex items-center gap-1">
                                            View <ExternalLink size={10} />
                                        </a>
                                    </td>
                                    <td className="p-3">{REASON_LABELS[r.reason] || r.reason}</td>
                                    <td className="p-3">{r.age_hours != null ? `${r.age_hours}h` : "—"}</td>
                                    <td className="p-3">
                                        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                            r.sla_state === "breached" ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"
                                        }`} data-testid={`sla-${r.id}`}>{r.sla_state}</span>
                                    </td>
                                    <td className="p-3 max-w-md truncate" title={r.details}>{r.details}</td>
                                    <td className="p-3 whitespace-nowrap">
                                        {r.status === "open" && (
                                            <button
                                                disabled={busy}
                                                onClick={() => act(r.id, "reviewing")}
                                                className="text-xs px-2 py-1 rounded-full border border-border hover:bg-muted mr-1"
                                                data-testid={`report-review-${r.id}`}
                                            >Review</button>
                                        )}
                                        <button
                                            disabled={busy}
                                            onClick={() => act(r.id, "resolved")}
                                            className="text-xs px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white mr-1"
                                            data-testid={`report-resolve-${r.id}`}
                                        >Resolve</button>
                                        <button
                                            disabled={busy}
                                            onClick={() => act(r.id, "dismissed")}
                                            className="text-xs px-2 py-1 rounded-full border border-border hover:bg-muted"
                                            data-testid={`report-dismiss-${r.id}`}
                                        >Dismiss</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
